import { useEffect, useState } from "react";
import styled from "@xstyled/styled-components";

import { SourcingPreferencesFieldProps } from "./interfaces";

import { Icon } from "@otta/icons";
import {
  Label,
  Text,
  Clickable,
  Input,
  Modal,
  Spacing,
  List,
} from "@otta/design";
import { TooltipWithIcon } from "@hire/components/TooltipWithIcon";
import { modularScale } from "@otta/design-tokens";

const Switch = styled(Clickable)`
  font-size: ${modularScale(-1)};
  text-decoration: underline;
`;

const Item = styled.div`
  flex-direction: column;
  display: flex;
  gap: 0.5rem;
`;

const ModalContent = styled.div`
  flex-direction: column;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  gap: 1.5rem;
`;

const Code = styled.code`
  background-color: gray-100;
  color: red-600;
  font-family: monospace;
`;

const SlightlyWiderModal = styled(Modal)`
  max-width: 40rem;
`;

/*
 * there's a default margin on list
 * that uses :not(:last-child) so is hard to override
 */
const Li = styled.li`
  margin-bottom: 0 !important;
`;

function HelpModal() {
  const [visible, setVisible] = useState(false);

  return (
    <>
      <Switch
        data-analytics-id="advanced-query-help"
        onClick={() => setVisible(true)}
      >
        Advanced query help
      </Switch>
      <SlightlyWiderModal open={visible} onOpenChange={v => setVisible(v)}>
        <ModalContent>
          <Text as={"h4"} size={2}>
            Advanced queries
          </Text>
          <Item>
            <Text bold>
              Separate keywords with spaces to find profiles containing them
              all.
            </Text>
            <Text>
              <Code>javascript react</Code>
            </Text>
            <Text>
              Will match only profiles containing <strong>both</strong>{" "}
              "javascript" and "react".
            </Text>
          </Item>
          <Item>
            <Text bold>
              Separate keywords with <Code>OR</Code> to find profiles containing
              any of them.
            </Text>
            <Text>
              <Code>javascript OR react</Code>
            </Text>
            <Text>
              Will match profiles containing <strong>either</strong>{" "}
              "javascript" or "react".
            </Text>
          </Item>
          <Item>
            <Text bold>Use quotes to search for exact phrases.</Text>
            <Text>
              <Code>"software engineer"</Code> (in quotes)
            </Text>
            <Text>
              Will match "software engineer" but not "engineer of software".
            </Text>
          </Item>
          <Item>
            <Text bold>Use brackets to control precedence.</Text>
            <Text>
              <Code>
                ("Account Manager" OR "Customer Success") Enterprise B2B
              </Code>
            </Text>
            <Text>Will match profiles containing either:</Text>
            <List type="bullet">
              <Li>"Account Manager" AND Enterprise AND B2B</Li>
              <Li>"Customer Success" AND Enterprise AND B2B</Li>
            </List>
          </Item>
          <Item>
            <Text bold>
              Use <Code>NOT</Code> to exclude profiles containing keywords
            </Text>
            <Text>
              <Code>NOT javascript</Code>
            </Text>
            <Text>
              Will only match profiles that <strong>do not</strong> include
              "javascript".
            </Text>
          </Item>
          <Item>
            <Text bold>Label keywords to search specific profile areas</Text>
            <List type="bullet">
              <Li>
                <Code>company:</Code> searches the candidate's previous
                companies
              </Li>
              <Li>
                <Code>education:</Code> searches the candidate's education
                history
              </Li>
              <Li>
                <Code>experience:</Code> searches the candidate's employment
                history
              </Li>
            </List>
            <Text>
              e.g. Find candidates who've used Java or worked at Salesforce:
            </Text>
            <Text>
              <Code>experience:java or company:salesforce</Code>
            </Text>
            <Text>
              You can label any part of the query, not just individual keywords:
            </Text>
            <Text>
              <Code>experience:((facebook or meta) and google)</Code>
            </Text>
          </Item>
        </ModalContent>
      </SlightlyWiderModal>
    </>
  );
}

function FieldLabel() {
  return (
    <TooltipWithIcon
      content="Search single words, multiple words, or multi-word phrases (e.g. “product design”)"
      icon={<Icon icon="circle-info" size={1} />}
    >
      <Label htmlFor="keywords">
        <Text bold>Keyword search</Text>
      </Label>
    </TooltipWithIcon>
  );
}

function AdvancedKeywordField({
  preferences,
  onChange,
}: SourcingPreferencesFieldProps): React.ReactElement {
  const [value, setValue] = useState(preferences.queryString);

  useEffect(() => {
    const int = setTimeout(() => onChange({ queryString: value }), 500);
    return () => clearTimeout(int);
  }, [value, onChange]);

  return (
    <Spacing size={-4}>
      <FieldLabel />
      <Input
        minLength={1}
        placeholder='Lead OR "Head of" OR Principal'
        defaultValue={value ?? ""}
        onChange={e => setValue(e.target.value)}
      />
      <HelpModal />
    </Spacing>
  );
}

export function KeywordsField({
  preferences,
  onChange,
}: SourcingPreferencesFieldProps): React.ReactElement {
  useEffect(() => {
    // clear out legacy keyword field
    if (preferences.keywords?.length) {
      onChange({ keywords: undefined });
    }
  }, [onChange, preferences]);

  return (
    <Spacing size={-1}>
      <AdvancedKeywordField preferences={preferences} onChange={onChange} />
    </Spacing>
  );
}
